import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { delay, tap, catchError } from 'rxjs/operators';
import {
  PrintSiteRef,
  CustomerType,
  CheckLayout,
} from '../../../core/models/reference.model';
import { getMockCheckLayouts } from '../../mocks/check-layouts.mock';
import { LoggingService } from '../../../core/services/logging.service';
import { BaseRepository } from './base.repository';
import envConfig from '../../../env-config';

@Injectable({ providedIn: 'root' })
export class ReferenceRepository extends BaseRepository {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggingService);
  private readonly apiUrl = envConfig.bffUrl ?? '';
  private readonly networkDelay = 200;

  /**
   * Gets all active print sites from the API.
   */
  getPrintSites(): Observable<PrintSiteRef[]> {
    this.logger.debug('ReferenceRepository: Fetching print sites');

    return this.http
      .get<PrintSiteRef[]>(`${this.apiUrl}/api/v1/print-sites`, {
        context: this.authContext(),
      })
      .pipe(
        tap(sites =>
          this.logger.debug('ReferenceRepository: Print sites fetched', {
            count: sites.length,
          })
        ),
        catchError(error => {
          this.logger.error(
            'ReferenceRepository: Failed to fetch print sites',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Gets all active customer types from the API.
   */
  getCustomerTypes(): Observable<CustomerType[]> {
    this.logger.debug('ReferenceRepository: Fetching customer types');

    return this.http
      .get<CustomerType[]>(`${this.apiUrl}/api/v1/customer-types`, {
        context: this.authContext(),
      })
      .pipe(
        tap(types =>
          this.logger.debug('ReferenceRepository: Customer types fetched', {
            count: types.length,
          })
        ),
        catchError(error => {
          this.logger.error(
            'ReferenceRepository: Failed to fetch customer types',
            error
          );
          throw error;
        })
      );
  }

  /**
   * Gets all check layouts (mock data for now).
   */
  getCheckLayouts(): Observable<CheckLayout[]> {
    const layouts = getMockCheckLayouts(false);
    return of(layouts).pipe(delay(this.networkDelay));
  }
}
